import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import './ScreenerWidget.css';
import ScreenerTable from '../../Components/Screener/ScreenerTable';
import {
  Criteria,
  ScreenerTableColumnType,
  SortingType,
  SavedScreener,
  ScreenerDataType,
  GetScreenerDataResponseType,
  CreateScreenerResult,
} from '../../types/screener.type';
import useScreenerColumn from '../../hooks/Screener/useScreenerColumn';
import useScreenerSectors from '../../hooks/Screener/useScreenerSectors';
import ScreenerMenu from '../../Components/Screener/ScreenerMenu';
import { MarketCriteriaItem } from '../../Components/Screener/Criteria/MarketCriteria/MarketCriteria';
import {
  defaultScreenerState,
  TableMode,
  MAX_SAVED_SCREENERS,
} from '../../constants/screener/screener.constant';
import { PRESET_STRATEGIES } from '../../constants/screener/presets.constant';
import { SectorCriteriaItem } from '../../Components/Screener/Criteria/SectorCriteria/SectorCriteria';
import { Paper, Typography, CircularProgress } from '@mui/material';
import AdvancedCriteriaPopup from '../../Components/Screener/Criteria/AdvancedCriteriaPopup/AdvancedCriteriaPopup';
import { defaultColumnVisibility } from '../../constants/screener/screener.constant';
import SPAdvancedCriteriaPopup from '../../Components/Screener/Criteria/SPAdvancedCriteriaPopup/SPAdvancedCriteriaPopup';
import ScreenerSaveDialog from '../../Components/Screener/ScreenerSaveDialog/ScreenerSaveDialog';
import { AC_TYPES } from '../../constants/screener/AC.types.constant';
import { getScreenerData } from '../../api/screener.api';
import {
  paginate,
  PaginationType,
  getMaxPageNum,
} from '../../utils/pagination';
import Pagination from '../../Components/Common/Pagination/Pagination';
import CommonTooltip from '../../Components/Common/CommonTooltip/CommonTooltip';
import {
  BaseWidgetProps,
  CommonTooltipType,
  WidgetMessage,
  WidgetHeightMessage,
} from '../../types/common.type';
import Loading from '../../Components/Loading/Loading';
import { ReactComponent as ErrorIcon } from '../../assets/icons/error-state.svg';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import { defaultPageNum, errorMsg } from '../../constants/common.constant';
import {
  Region,
  ThemeType,
  WidgetMessageType,
  WidgetNameSource,
} from '../../utils/type';
import Layout from '../../Components/Common/Layout/Layout';
import { THEME_COLOR_MAPPING } from '../../utils/constant';
import { StockData } from '../../types/indices.type';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import useContainerWidth from '../../hooks/useContainerWidth';
import { getCssVariable } from '../../utils/utils';
import { formatLocaleNumber } from '../../helpers/number';

export interface ScreenerWidgetProps extends BaseWidgetProps {
  token?: string;
  region?: Region[];
  theme?: ThemeType;
  onCallback?: (message: StockData) => void;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

const STORAGE_KEY = 'bell-widget-saved-screeners';

const ScreenerWidget = ({
  token = '',
  region = [Region.AU],
  onLoadingChange,
  onError,
  theme = ThemeType.Direct,
  onCallback,
  onNoData,
  onHeightChange,
}: ScreenerWidgetProps) => {
  theme = Object.values(ThemeType).includes(theme) ? theme : ThemeType.Direct;
  const themeColor = useMemo(
    () => getCssVariable('--color-brand', theme),
    [theme],
  );
  const widgetRef = useRef<HTMLDivElement | null>(null);
  useResizeNotifier(widgetRef, WidgetNameSource.Screener, onHeightChange);
  const { width, isSmallScreen } = useContainerWidth(widgetRef);
  const tempCriteriaRef = useRef<Criteria[]>(defaultScreenerState.criteria);
  const msgItem = errorMsg.find((obj) => obj.key === 'stockScreener');

  const [isLoading, setIsLoading] = useState(true);
  const onLoadingChangeRef = useRef(onLoadingChange);
  onLoadingChangeRef.current = onLoadingChange;
  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);
  const [isEmptyData, setIsEmptyData] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const [data, setData] = useState<ScreenerDataType[]>([]);
  const [tableMode, setTableMode] = useState<TableMode>(TableMode.NORMAL);
  const [markets, setMarkets] = useState<MarketCriteriaItem[]>(
    defaultScreenerState.markets,
  );
  const { sectors: baselineSectors, isLoaded: sectorsLoaded } =
    useScreenerSectors(token);
  const [sectors, setSectors] = useState<SectorCriteriaItem[]>([]);
  useEffect(() => {
    setSectors(baselineSectors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baselineSectors]);
  const [criteria, setCriteria] = useState<Criteria[]>(
    defaultScreenerState.criteria,
  );
  const [tempCriteria, setTempCriteria] = useState<Criteria[]>(
    defaultScreenerState.criteria,
  );
  const [openAdvancedCriteria, setOpenAdvancedCriteria] = useState(false);
  const [openSPAdvancedCriteria, setOpenSPAdvancedCriteria] = useState(false);
  const [screenerName, setScreenerName] = useState('');
  const [savedScreeners, setSavedScreeners] = useState<SavedScreener[]>([]);
  const [currentScreenerId, setCurrentScreenerId] = useState<string | null>(
    null,
  );
  const [currentPresetId, setCurrentPresetId] = useState<string | null>(null);
  const [columnVisibility, setColumnVisibility] = useState<{
    [key: string]: boolean;
  }>(defaultColumnVisibility);
  const [skipApiCall, setSkipApiCall] = useState(false);

  const [maxPageNum, setMaxPageNum] = useState(defaultPageNum);
  const RESULTS_PER_PAGE = useMemo(
    () => (isSmallScreen ? 10 : 20),
    [isSmallScreen],
  );
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState<PaginationType>(
    paginate(page, RESULTS_PER_PAGE, total, maxPageNum),
  );

  useEffect(() => {
    if (width > 0) {
      setMaxPageNum(getMaxPageNum(width, false, false, false));
    }
  }, [width]);

  const [sorting, setSorting] = useState<SortingType>({
    id: 'ticker',
    asc: true,
  });
  const [resetTrigger, setResetTrigger] = useState(0);

  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [tooltip, setTooltip] = useState<CommonTooltipType>({
    content: '',
    show: false,
    x: 0,
    y: 0,
  });

  const isInitialMount = useRef(true);
  const isInitializing = useRef(true);
  const isResettingSorting = useRef(false);

  // Function to save all screeners to local storage
  const saveScreenersToLocalStorage = useCallback(
    (screeners: SavedScreener[]) => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(screeners));
        setSavedScreeners(screeners);
      } catch (error) {
        console.error('Failed to save screeners to localStorage:', error);
      }
    },
    [],
  );

  // Function to get all saved screeners from local storage
  const loadSavedScreeners = useCallback((): SavedScreener[] => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) return [];
      const parsed = JSON.parse(saved) as SavedScreener[];
      // Migrate away from the legacy fixed-10-slot model: drop blank
      // placeholder slots that used to be pre-allocated on first load.
      const migrated = parsed.filter(
        (screener) => !(screener.isInitial && screener.name === 'New Screener'),
      );
      migrated.forEach((screener) => {
        if (screener.isInitial === undefined) {
          screener.isInitial = false;
        }
      });
      return migrated.slice(0, MAX_SAVED_SCREENERS);
    } catch (error) {
      console.error('Failed to load saved screeners from localStorage:', error);
      return [];
    }
  }, []);

  const buildScreenerPayload = (
    criteria: Criteria[],
    markets: MarketCriteriaItem[],
    sectors: SectorCriteriaItem[],
  ) => ({
    criteria: criteria
      .filter((c) => c.selected)
      .map((c) => ({
        id: c.id,
        timeValue: c.timeValue,
        selectedMin: c.selectedMin,
        selectedMax: c.selectedMax,
      })),
    markets: markets.filter((m) => m.selected).map((m) => m.value),
    sectors: sectors.filter((s) => s.selected).map((s) => s.value),
  });

  // Function to create a new saved screener
  const createScreener = useCallback(
    (
      name: string,
      criteria: Criteria[],
      markets: MarketCriteriaItem[],
      sectors: SectorCriteriaItem[],
    ): CreateScreenerResult => {
      const existingScreeners = loadSavedScreeners();

      if (existingScreeners.length >= MAX_SAVED_SCREENERS) {
        return { success: false, reason: 'limit' };
      }

      const newScreener: SavedScreener = {
        id: `${Date.now()}`,
        isInitial: false,
        name,
        ...buildScreenerPayload(criteria, markets, sectors),
      };

      saveScreenersToLocalStorage([...existingScreeners, newScreener]);
      setCurrentPresetId(null);
      setCurrentScreenerId(newScreener.id);
      setScreenerName(name);

      return { success: true, id: newScreener.id };
    },
    [loadSavedScreeners, saveScreenersToLocalStorage],
  );

  // Function to update an existing saved screener in place
  const updateScreener = useCallback(
    (
      id: string,
      name: string,
      criteria: Criteria[],
      markets: MarketCriteriaItem[],
      sectors: SectorCriteriaItem[],
    ): boolean => {
      const existingScreeners = loadSavedScreeners();
      const index = existingScreeners.findIndex((s) => s.id === id);

      if (index === -1) {
        console.error('Screener not found');
        return false;
      }

      const updatedScreener: SavedScreener = {
        ...existingScreeners[index],
        name,
        ...buildScreenerPayload(criteria, markets, sectors),
      };

      const updatedScreeners = [...existingScreeners];
      updatedScreeners[index] = updatedScreener;
      saveScreenersToLocalStorage(updatedScreeners);

      if (id === currentScreenerId) {
        setScreenerName(name);
      }

      return true;
    },
    [loadSavedScreeners, saveScreenersToLocalStorage, currentScreenerId],
  );

  useEffect(() => {
    const loadedScreeners = loadSavedScreeners();
    setSavedScreeners(loadedScreeners);
    isInitialMount.current = false;
    // No screener/preset is auto-selected on mount anymore, so nothing else
    // will flip this - unblock the initial results fetch here instead.
    isInitializing.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadSavedScreeners]);

  // Function to load a screener by id
  const loadScreener = useCallback(
    (id: string): boolean => {
      const existingScreeners = loadSavedScreeners();
      const screener = existingScreeners.find((s) => s.id === id);

      if (!screener) {
        console.error('Screener not found');
        return false;
      }

      if (screener.criteria && Array.isArray(screener.criteria)) {
        const currentCriteria: Criteria[] = JSON.parse(
          JSON.stringify(defaultScreenerState.criteria),
        );
        screener.criteria.forEach((c) => {
          const index = currentCriteria.findIndex((item) => item.id === c.id);
          if (index !== -1) {
            currentCriteria[index] = {
              ...currentCriteria[index],
              selected: true,
              timeValue: c.timeValue || '',
              selectedMin: c.selectedMin,
              selectedMax: c.selectedMax,
            };
          }
        });
        setCriteria(currentCriteria);
        setTempCriteria(currentCriteria);
      }

      // Reconstruct markets
      if (screener.markets && Array.isArray(screener.markets)) {
        const selectedMarkets = JSON.parse(
          JSON.stringify(defaultScreenerState.markets),
        ).map((m: MarketCriteriaItem) => ({
          ...m,
          selected: screener.markets.includes(m.value),
        }));
        setMarkets(selectedMarkets);
      } else {
        setMarkets(defaultScreenerState.markets);
      }

      // Reconstruct sectors
      if (screener.sectors && Array.isArray(screener.sectors)) {
        const selectedSectors = JSON.parse(
          JSON.stringify(baselineSectors),
        ).map((s: SectorCriteriaItem) => ({
          ...s,
          selected: screener.sectors.includes(s.value),
        }));
        setSectors(selectedSectors);
      } else {
        setSectors(baselineSectors);
      }
      setScreenerName(screener.name);
      setCurrentPresetId(null);
      isInitializing.current = false;
      return true;
    },
    [loadSavedScreeners, baselineSectors],
  );

  // Function to apply a preset strategy's criteria/sector/sort
  const handleSelectPreset = useCallback(
    (presetId: string): boolean => {
      const preset = PRESET_STRATEGIES.find((p) => p.id === presetId);
      if (!preset) {
        console.error('Preset not found');
        return false;
      }

      const currentCriteria: Criteria[] = JSON.parse(
        JSON.stringify(defaultScreenerState.criteria),
      );
      preset.criteria.forEach((pc) => {
        const index = currentCriteria.findIndex((item) => item.id === pc.id);
        if (index !== -1) {
          currentCriteria[index] = {
            ...currentCriteria[index],
            selected: true,
            timeValue: pc.timeValue ?? currentCriteria[index].timeValue,
            selectedMin: pc.selectedMin,
            selectedMax: pc.selectedMax,
          };
        }
      });
      setCriteria(currentCriteria);
      setTempCriteria(currentCriteria);
      setSectors(baselineSectors);

      // Always reset sorting for a preset switch - a preset without an
      // explicit sort should not inherit whatever was left over from the
      // previously selected preset/screener.
      setSorting(
        preset.sort
          ? {
              id: preset.sort.id,
              option: preset.sort.option,
              asc: preset.sort.asc,
            }
          : { id: 'ticker', asc: true },
      );

      setScreenerName(preset.name);
      setCurrentScreenerId(null);
      setCurrentPresetId(presetId);
      isInitializing.current = false;
      return true;
    },
    [baselineSectors],
  );

  // Update columnVisibility based on selected criteria
  useEffect(() => {
    const selectedCriteria = criteria.filter((item: Criteria) => item.selected);

    // Example mapping logic - adjust based on your actual criteria
    setColumnVisibility((prevVisibility) => {
      const newVisibility = { ...prevVisibility };

      if (isSmallScreen && selectedCriteria.length > 0) {
        // On small screens, only show the first selected criterion (or the one that's already visible)
        // const currentlyVisibleCriterion = selectedCriteria.find(c => prevVisibility[c.id] !== false);
        // const criterionToShow = currentlyVisibleCriterion || selectedCriteria[0];

        // // Set all to false first
        // selectedCriteria.forEach((criterion) => {
        //     newVisibility[criterion.id] = false;
        // });

        // // Then set only one to true
        // if (criterionToShow) {
        //     newVisibility[criterionToShow.id] = true;
        // }

        selectedCriteria.forEach((criterion) => {
          newVisibility[criterion.id] = true;
        });

        // newVisibility["criteria-selector"] = true;
      } else {
        // On large screens, show all selected criteria
        selectedCriteria.forEach((criterion) => {
          newVisibility[criterion.id] = true;
        });

        // Hide the criteria selector column on large screens
        // newVisibility["criteria-selector"] = false;
      }

      return newVisibility;
    });
  }, [criteria, isSmallScreen]);

  useEffect(() => {
    if (isSmallScreen) {
      setOpenAdvancedCriteria(false);
    } else {
      setOpenSPAdvancedCriteria(false);
    }
  }, [isSmallScreen]);

  // Update criteria when currentScreenerId changes (but not on initial mount)
  useEffect(() => {
    if (skipApiCall) {
      setSkipApiCall(false);
      return;
    }

    if (currentScreenerId && !isInitialMount.current) {
      loadScreener(currentScreenerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScreenerId]);

  const selectedSectors = sectors.filter((s) => s.selected);
  const selectedSectorIds = (
    selectedSectors.length > 0 ? selectedSectors : sectors
  )
    .map((s) => s.value)
    .join(';');

  // Reset page to 1 when criteria, region, or sector changes
  useEffect(() => {
    if (!isInitialMount.current && !isInitializing.current) {
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteria, region?.join(';'), selectedSectorIds, RESULTS_PER_PAGE]);

  useEffect(() => {
    setPagination(paginate(page, RESULTS_PER_PAGE, total, maxPageNum));
  }, [total, page, maxPageNum]);

  const resultsRequestId = useRef(0);

  useEffect(() => {
    if (isInitialMount.current || isInitializing.current || !sectorsLoaded) {
      return;
    }

    // Check if current sorting id is an advanced criteria that was removed
    const criterion = criteria.find((c) => c.id === sorting.id);
    if (criterion && sorting.id !== 'ticker' && !criterion.selected) {
      // Reset sorting to ticker and skip API call - will trigger on next render
      isResettingSorting.current = true;
      setSorting({ id: 'ticker', asc: true });
      return;
    }

    // Clear the flag if it was set (we've already reset sorting)
    if (isResettingSorting.current) {
      isResettingSorting.current = false;
    }

    const c: any[] = [];
    criteria.forEach((item: Criteria) => {
      if (item.selected) {
        const apiId = item.id.split('_')[0];
        if (item.withRange) {
          c.push({
            id: apiId,
            fr: item.selectedMin,
            to: item.selectedMax,
            option: item.timeValue,
          });
        } else if (item.withScoreRange) {
          c.push({
            id: apiId,
            fr: item.selectedMin,
            to: item.selectedMax,
          });
        } else if (item.type === AC_TYPES.time) {
          c.push({ id: apiId, option: item.timeValue });
        } else {
          c.push({ id: apiId });
        }
      }
    });
    setIsLoading(true);
    setIsEmptyData(false);
    const requestId = ++resultsRequestId.current;
    getScreenerData(
      {
        page: { no: page, size: RESULTS_PER_PAGE },
        country: region && region.length > 0 ? region.join(';') : Region.AU,
        sector: selectedSectorIds,
        advanced: c,
        sort: { ...sorting, id: sorting.id.split('_')[0] },
      },
      token,
    )
      .then((response: GetScreenerDataResponseType) => {
        // Ignore stale responses from a superseded request
        if (requestId !== resultsRequestId.current) return;
        setData(response.data);
        setTotal(response.total);
        setIsLoading(false);
        setIsEmptyData(response.total === 0);
        if (response.total === 0) {
          onNoData?.({
            type: WidgetMessageType.NoData,
            source: 'ScreenerWidget',
            data: null,
          });
        }
      })
      .catch((error: any) => {
        if (requestId !== resultsRequestId.current) return;
        console.log('error', error);
        setIsLoading(false);
        setIsEmptyData(true);
        onError?.({
          message: error.message,
          type: error.name + '_' + error.code,
        });
      })
      .finally(() => {});
  }, [
    page,
    criteria,
    region?.join(';'),
    selectedSectorIds,
    sectorsLoaded,
    token,
    sorting,
    resetTrigger,
    RESULTS_PER_PAGE,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setTempCriteria(criteria);
    tempCriteriaRef.current = criteria;
  }, [openAdvancedCriteria, openSPAdvancedCriteria, criteria]);

  const handleChildUpdate = (newCriteria: Criteria[]) => {
    setTempCriteria(newCriteria);
    tempCriteriaRef.current = newCriteria;
  };

  // Function to apply the temporary criteria and close the popup
  const handleGetResults = (isSP?: boolean) => {
    const latestData = tempCriteriaRef.current;
    if (isSP) {
      setOpenSPAdvancedCriteria(false);
    } else {
      setOpenAdvancedCriteria(false);
    }
    setCriteria(latestData);
  };

  // Function to cancel and reset to original criteria
  const handleCancelAdvancedCriteria = (isSP?: boolean) => {
    if (isSP) {
      setOpenSPAdvancedCriteria(false);
    } else {
      setOpenAdvancedCriteria(false);
    }
  };

  const handleRefresh = () => {
    setResetTrigger((prev) => prev + 1);
  };

  const handleResetAll = () => {
    setCurrentScreenerId(null);
    setCurrentPresetId(null);
    setSectors(baselineSectors);
    setCriteria(defaultScreenerState.criteria);
    setTempCriteria(defaultScreenerState.criteria);
    tempCriteriaRef.current = defaultScreenerState.criteria;
    setScreenerName('');
    setSkipApiCall(false);
  };

  // Mobile "Screener Criteria" popup Reset All: saved strategy/sector reset
  // immediately, but criteria only staged into tempCriteria — the actual
  // applied `criteria` only changes once the user confirms via Get Results;
  // Back/Cancel must leave the currently applied results untouched.
  const handleResetAllStaged = () => {
    setCurrentScreenerId(null);
    setCurrentPresetId(null);
    setSectors(baselineSectors);
    setTempCriteria(defaultScreenerState.criteria);
    tempCriteriaRef.current = defaultScreenerState.criteria;
    setScreenerName('');
  };

  const handleDeleteScreener = (id: string) => {
    const existingScreeners = loadSavedScreeners();
    const updatedScreeners = existingScreeners.filter((s) => s.id !== id);
    saveScreenersToLocalStorage(updatedScreeners);
    if (id === currentScreenerId) {
      handleResetAll();
    }
  };

  const handleOnClick = useCallback((symbol: string) => {
    if (onCallback && symbol) {
      let message = {
        data: { symbol: symbol },
        source: 'screener',
        type: WidgetMessageType.OnStockClick,
      };
      if ((window as any).ReactNativeWebView) {
        (window as any).ReactNativeWebView.postMessage(
          JSON.stringify(message),
          '*',
        );
      }
      window.parent.postMessage(JSON.stringify(message), '*');
      onCallback(message);
    }
  }, [onCallback]);

  const columns: ScreenerTableColumnType[] = useScreenerColumn({
    mode: tableMode,
    selectedCriteria: criteria.filter((item: Criteria) => item.selected),
    allCriteria: criteria,
    region: region.join(';'),
    page,
    size: RESULTS_PER_PAGE,
    sorting,
    setSorting,
    token: token || '',
    columnVisibility,
    setColumnVisibility,
    tooltipRef,
    setTooltip,
    isSmallScreen,
    onStockClick: handleOnClick,
  });
  return (
    <Layout theme={theme}>
      <div
        ref={widgetRef}
        className={`screener-container${
          openAdvancedCriteria ? ' screener-container-criteria-open' : ''
        }`}
      >
        <div className="screener">
          <ScreenerMenu
            themeColor={themeColor}
            isSmallScreen={isSmallScreen}
            setOpenAdvancedCriteria={setOpenAdvancedCriteria}
            setOpenSPAdvancedCriteria={setOpenSPAdvancedCriteria}
            currentScreenerId={currentScreenerId}
            setCurrentScreenerId={setCurrentScreenerId}
            currentPresetId={currentPresetId}
            onSelectPreset={handleSelectPreset}
            screenerName={screenerName}
            savedScreeners={savedScreeners}
            criteria={criteria}
            markets={markets}
            sectors={sectors}
            setSectors={setSectors}
            onRefresh={handleRefresh}
            onResetAll={handleResetAll}
            onCreateScreener={createScreener}
            onUpdateScreener={updateScreener}
            onDeleteScreener={handleDeleteScreener}
            setSkipApiCall={setSkipApiCall}
          />
          <Paper
            elevation={0}
            sx={{
              boxSizing: 'border-box',
              textAlign: 'start',
              padding: isSmallScreen ? '16px 16px' : '16px 32px 8px 32px',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: '16px',
                fontWeight: '400',
                lineHeight: '24px',
                color: '#1E1F2F',
              }}
            >
              {`Showing ${formatLocaleNumber(data && data.length > 0 ? (page - 1) * RESULTS_PER_PAGE + 1 : 0)}-${formatLocaleNumber(Math.min(page * RESULTS_PER_PAGE, total))} of ${formatLocaleNumber(total)} results`}
            </Typography>
          </Paper>
          {isEmptyData && !isLoading ? (
            <div style={{ height: 600 }}>
              <ErrorMessage msg={msgItem} />
            </div>
          ) : (
            <div className="screener-table-pagination-wrapper">
              {isLoading && data.length === 0 && !onLoadingChange ? (
                <Loading />
              ) : (
                <>
                  {isLoading && !onLoadingChange && (
                    <div className="screener-table-loading-overlay">
                      <Loading />
                    </div>
                  )}
                  <ScreenerTable
                    columns={columns}
                    data={data}
                    columnVisibility={columnVisibility}
                    sorting={sorting}
                    setSorting={setSorting}
                    token={token}
                    isSmallScreen={isSmallScreen}
                    isLoading={isLoading}
                  />
                </>
              )}
              <div className="screener-pagination-container">
                <Pagination
                  pagination={pagination}
                  onPageClick={(targetPage: number) => {
                    setPage(targetPage);
                  }}
                  haveLastPage={true}
                />
              </div>
            </div>
          )}

          {openAdvancedCriteria && (
            <>
              <div className="screener-advanced-criteria-backdrop" />
              <AdvancedCriteriaPopup
                region={region.join(';')}
                sector={selectedSectorIds}
                tempCriteria={tempCriteria}
                setTempCriteria={handleChildUpdate}
                setOpenAdvancedCriteria={setOpenAdvancedCriteria}
                onGetResults={() => handleGetResults(false)}
                onCancel={() => handleCancelAdvancedCriteria(false)}
                token={token}
              />
            </>
          )}
        </div>

        {isSmallScreen && openSPAdvancedCriteria && (
          <SPAdvancedCriteriaPopup
            themeColor={themeColor}
            region={region.join(';')}
            sector={selectedSectorIds}
            token={token || ''}
            tempCriteria={tempCriteria}
            setTempCriteria={handleChildUpdate}
            setOpenSPAdvancedCriteria={setOpenSPAdvancedCriteria}
            sectors={sectors}
            setSectors={setSectors}
            onGetResults={() => handleGetResults(true)}
            onCancel={() => handleCancelAdvancedCriteria(true)}
            onResetAll={handleResetAllStaged}
          />
        )}
        <CommonTooltip
          ref={tooltipRef}
          content={tooltip.content}
          show={tooltip.show}
          x={tooltip.x}
          y={tooltip.y}
        />
      </div>
    </Layout>
  );
};

export default ScreenerWidget;
